import type { ColumnDef } from "@tanstack/react-table";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { buildActionColumn, buildTwoLineColumn } from "@/components/Table";
import type { Action } from "@/components/MenuButton";
import type { CoverageGroupBy, CoverageGroupMember, CoverageGroupRow } from "../types";
import { coverageMembersCsv, formatCoveragePrimary } from "./groupCoverage";

type GroupColumnsOptions = {
  groupBy: CoverageGroupBy;
  groupColumnHeader: string;
  addActionLabel: string;
  memberAddActionLabel: string;
  removeActionLabel: string;
  setMenu: (menu: TableRowMenu) => void;
  onAdd: (group: CoverageGroupRow) => void;
  onAddMember: (member: CoverageGroupMember) => void;
  onRemoveMember: (member: CoverageGroupMember) => void;
};

function groupOverflowActions({
  group,
  groupBy,
  addActionLabel,
  memberAddActionLabel,
  removeActionLabel,
  onAdd,
  onAddMember,
  onRemoveMember
}: {
  group: CoverageGroupRow;
  groupBy: CoverageGroupBy;
  addActionLabel: string;
  memberAddActionLabel: string;
  removeActionLabel: string;
  onAdd: (group: CoverageGroupRow) => void;
  onAddMember: (member: CoverageGroupMember) => void;
  onRemoveMember: (member: CoverageGroupMember) => void;
}): Action[] {
  const actions: Action[] = [];

  if (group.canAdd) {
    actions.push({
      text: addActionLabel,
      onClick: () => onAdd(group)
    });
  }

  for (const member of group.members) {
    if (member.person) {
      const targetName = groupBy === "consultant" ? member.sectionName : member.person.name;
      actions.push({
        text: `${removeActionLabel} ${targetName}`,
        iconName: "delete",
        destructive: true,
        onClick: () => onRemoveMember(member)
      });
      continue;
    }

    actions.push({
      text: `${memberAddActionLabel} — ${member.sectionName}`,
      onClick: () => onAddMember(member)
    });
  }

  return actions;
}

export function getCoverageGroupColumns({
  groupBy,
  groupColumnHeader,
  addActionLabel,
  memberAddActionLabel,
  removeActionLabel,
  setMenu,
  onAdd,
  onAddMember,
  onRemoveMember
}: GroupColumnsOptions): ColumnDef<CoverageGroupRow, unknown>[] {
  return [
    {
      ...buildTwoLineColumn<CoverageGroupRow>({
        accessorKey: "label",
        header: groupColumnHeader,
        getPrimaryText: (row) => formatCoveragePrimary(row.label, row.location),
        getSecondaryText: (row) => coverageMembersCsv(row, groupBy),
        size: 480
      }),
      enableSorting: false
    },
    buildActionColumn<CoverageGroupRow>({
      getActions: (row) =>
        groupOverflowActions({
          group: row,
          groupBy,
          addActionLabel,
          memberAddActionLabel,
          removeActionLabel,
          onAdd,
          onAddMember,
          onRemoveMember
        }),
      actionId,
      setMenu
    })
  ];
}
